import { useState } from 'react'
import { useSweepstakes } from '../../contexts/SweepstakesContext'
import { Calendar, MapPin, ChevronLeft } from 'lucide-react'
import Input from '../ui/Input'
import Button from '../ui/Button'
import CityAutocomplete from '../ui/AddressAutocomplete'

/**
 * Step4WeddingDetails
 * Fourth step: Collect wedding date and location
 */
const Step4WeddingDetails = () => {
  const {
    formData,
    updateFormData,
    goToNextStep,
    goToPreviousStep,
    errors
  } = useSweepstakes()

  const [isTBA, setIsTBA] = useState(formData.weddingDate === 'TBA')

  const handleNext = () => {
    goToNextStep()
  }

  const handleBack = () => {
    goToPreviousStep()
  }

  const handleTBAToggle = () => {
    const newTBA = !isTBA
    setIsTBA(newTBA)
    if (newTBA) {
      updateFormData('weddingDate', 'TBA')
    } else {
      updateFormData('weddingDate', '')
    }
  }

  const handleLocationSelect = (cityData) => {
    // Format as "City, State" for storage
    const formatted = `${cityData.city}, ${cityData.state}`
    updateFormData('weddingLocation', formatted)
  }

  return (
    <div className="space-y-6">
      {/* Question */}
      <div className="text-center mb-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Tell us about your wedding
        </h3>
        <p className="text-sm text-gray-600">
          This helps us match you with the perfect photographer
        </p>
      </div>

      {/* Inputs */}
      <div className="space-y-4">
        {/* Wedding Date */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-dusty-700">
              Wedding Date
            </label>
            <button
              type="button"
              onClick={handleTBAToggle}
              className={`
                text-xs px-3 py-1 rounded-full transition-colors
                ${isTBA
                  ? 'bg-purple-100 text-purple-700 font-medium'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }
              `}
            >
              {isTBA ? 'TBA Selected' : 'Mark as TBA'}
            </button>
          </div>

          {!isTBA ? (
            <Input
              type="text"
              placeholder="e.g., June 15, 2025 or Summer 2025"
              value={formData.weddingDate}
              onChange={(e) => updateFormData('weddingDate', e.target.value)}
              error={errors.weddingDate}
              icon={<Calendar className="w-4 h-4 text-gray-400" />}
              autoFocus
            />
          ) : (
            <div className="w-full px-3 py-2.5 border-2 border-dashed border-purple-300 rounded-lg bg-purple-50 text-center">
              <span className="text-sm text-purple-700 font-medium">
                Wedding date to be announced
              </span>
            </div>
          )}
        </div>

        {/* Wedding Location */}
        <div>
          <label className="block text-sm font-medium text-dusty-700 mb-1.5">
            City & State of Wedding
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
              <MapPin className="w-4 h-4 text-gray-400" />
            </div>
            <CityAutocomplete
              value={formData.weddingLocation}
              onChange={(e) => updateFormData('weddingLocation', e.target.value)}
              onCitySelect={handleLocationSelect}
              placeholder="Start typing city name..."
              className="pl-10"
              required
            />
          </div>
          {errors.weddingLocation && (
            <p className="mt-1.5 text-sm text-red-600">{errors.weddingLocation}</p>
          )}
          <p className="text-xs text-gray-500 mt-1">
            Select your city from the dropdown for best results
          </p>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex gap-3 pt-4">
        <Button
          onClick={handleBack}
          className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold transition-colors"
        >
          <ChevronLeft className="w-4 h-4 inline mr-1" />
          Back
        </Button>
        <Button
          onClick={handleNext}
          className="flex-1 bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-lg font-semibold transition-colors"
          disabled={!formData.weddingDate.trim() || !formData.weddingLocation.trim()}
        >
          Continue
        </Button>
      </div>
    </div>
  )
}

export default Step4WeddingDetails
